/// <amd-module name="Wiki/PageTree/WikiPageTreeItemProvider" />
import { endsWith, localeIgnoreCaseComparer, startsWith } from "VSS/Core/Util/String";
import { ITreeItem, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { WikiPage } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { WikiTreePage } from "Wiki/PageTree/Contracts";
import { getParentPaths } from "Wiki/PageTree/Helper";
import { RepoConstants, TemplateConstants } from "Wiki/ViewCommon/Constants";
import { getFileName, getParentPath } from "Wiki/ViewCommon/Utils/PathHelper";
import { boundedOrderIncrement } from "Wiki/ViewCommon/Utils/WikiHelper";

export class WikiPageTreeItemProvider extends TreeItemProvider<WikiTreePage> {
    private _treeMapper: PathTreeItemMapper;

    private _allTreeItems: { [key: string]: ITreeItem<WikiTreePage> } = {};

    // We need case in-sensitive lookup on wiki page paths in some scenarios e.g. wiki markdown-renderer.
    // Note: We cannot use `_allTreeItems` as we need to keep key case-sensitive for supporting
    // case sensitive git scenarios.
    // Do not use this map for any other purpose.
    private _wikiPathIgnoreCaseToPageMap: { [key: string]: WikiPage } = {};

    constructor(initialItems: WikiPage[], defaultExpand: boolean) {
        super([]); // TODO: this will be later loaded with initial set of pages
        initialItems = this._filterHiddenFiles(initialItems);
        const isItemExpanded = (item: WikiTreePage | undefined): boolean => {
            return defaultExpand;
        };

        this._treeMapper = new PathTreeItemMapper({
            hasChildItems: this._hasChildItems,
            isItemExpanded: isItemExpanded
        });

        const result = this._treeMapper.createPageTree(initialItems, this._allTreeItems);
        this._addWikiPagesToWikiPathIgnoreCaseMap(this._allTreeItems);

        if (result.length) {
            this.splice(undefined, undefined, [{ items: result }]);
        }
    }

    public reorderItem(path: string, newOrder: number): void {
        const itemToReorder = this._allTreeItems[path];
        if (!itemToReorder || itemToReorder.data.order === newOrder) {
            return;
        }

        const parentPath = getParentPath(path);
        const parentItem = this._allTreeItems[parentPath]; // in case parentpath is '/' - parentitem is undefined
        const itemBefore = this._reorderSiblings(path, itemToReorder.data.order, newOrder);

        // remove the item and add it back later
        this.remove(itemToReorder, parentItem);
        this.add(itemToReorder, parentItem, itemBefore);
    }

    /**
     * This method is optimized for using when we want to move from partial tree to complete tree
     * using 'replaceAllItems' in this case, will remove all items from tree and insert them back again
     * this method instead adds the items to existing tree
     * this assumes that first level hierarchy always exists (and adds to it)
     * if there is a hierarchy posible - none of whose ancestors will be present in the partial tree, DO NOT use this method
     */
    public completeExistingItems(newItems: WikiPage[]): void {
        const allTreeItems: { [key: string]: ITreeItem<WikiTreePage> } = {};
        newItems = this._filterHiddenFiles(newItems);
        this._treeMapper.createPageTree(newItems, allTreeItems);

        for (const path in this._allTreeItems) {
            const parent = this._allTreeItems[path];
            const newTree = allTreeItems[path];
            // we dont want to add child items, if this hierarchy was already complete
            if (parent && parent.childItems && parent.childItems.length === 0 && newTree && newTree.childItems) {
                this.splice(parent, undefined, [{ items: newTree.childItems }]);
            }
        }

        for (const path in allTreeItems) {
            if (this._allTreeItems[path] === undefined) {
                this._addWikiPageTreeItem(path, allTreeItems[path]);
            } else {
                // if the item already exists, still override the underlying page
                this._allTreeItems[path].data = allTreeItems[path].data;
                this._wikiPathIgnoreCaseToPageMap[path.toLocaleLowerCase()] = allTreeItems[path].data;
            }
        }
    }

    /**
     * Removes all exisiting items from tree and adds new ones in correct hierarchy
     * @param newItems: items to be added
     * @param maintainExpandBehaviour : extra cost if you want to maintain expanded items
     */
    public replaceAllItems(newItems: WikiPage[], maintainExpandBehaviour?: boolean): void {
        const previousVisibleItems = this.value.slice(); // this could be drastically less than _allTreeItems
        this.clear();
        newItems = this._filterHiddenFiles(newItems);
        const result = this._treeMapper.createPageTree(newItems, this._allTreeItems);
        this._addWikiPagesToWikiPathIgnoreCaseMap(this._allTreeItems);

        if (result.length) {
            // expand all items expanded previously
            if (maintainExpandBehaviour) {
                // we use previousVisibleItems and not previousAlltreeItems becasue any previously visible item would be avialable here
                // we will lose cases where child items were expanded but parent was collapsed - but should be OK
                for (const item of previousVisibleItems) {
                    if (item.underlyingItem.expanded) {
                        const currentItem = this._allTreeItems[item.underlyingItem.data.path];
                        if (!currentItem) {
                            if ($DEBUG) {
                                throw new Error("item expanded but does not exist");
                            }
                        }

                        currentItem.expanded = true;
                    }
                }
            }
            this._updateConformancyForPages();
            this.splice(undefined, undefined, [{ items: result }]);
        }
    }

    public deletePage(pagePath: string): void {
        if (pagePath === RepoConstants.RootPath) {
            this.clear();
            return;
        }

        const item = this._allTreeItems[pagePath];
        if (!item) {
            return; // nothing if the page doesn't exist
        }

        const parentPath = getParentPath(pagePath);
        const parentItem = this._allTreeItems[parentPath];

        // if we dont re-calcualte orders, parent page will have different orders on server and client in this session
        const reorderSiblings = (siblings: WikiPage[]): WikiPage[] => {
            const siblingsArrary: WikiPage[] = [];
            const orderOfPageToBeDeleted = item.data.order || 0;

            for (const sibling of siblings) {
                sibling.order = sibling.order || 0;
                if (sibling.path !== pagePath) {
                    if (sibling.order > orderOfPageToBeDeleted) {
                        sibling.order = sibling.order - 1;
                    }
                    siblingsArrary.push(sibling);
                }
            }

            return siblingsArrary;
        };

        let siblings: WikiPage[] = [];
        if (item && parentPath === RepoConstants.RootPath) {
            // calculate all root pages:
            // using this.value and not this._allTreeItems as root items will always be in view
            this.value.forEach(item => {
                if (!item.parentItem) {
                    siblings.push(item.underlyingItem.data);
                }
            });
        } else if (parentItem && item) {
            siblings = parentItem.data.subPages;
        }
        if (siblings.length > 0) {
            reorderSiblings(siblings);
        }

        if (parentItem) {
            const newSubpages = parentItem.data.subPages.filter(p => p.path != pagePath);
            parentItem.data.subPages = newSubpages;

            if (parentItem.data.subPages.length === 0) {
                parentItem.data.isParentPage = false;
            }
        }

        const allChildPaths = this._getAllSubPageList(pagePath);
        // relative order will not change - so we are good to go
        this.splice(parentItem, [item], undefined);

        for (const childPath of allChildPaths) {
            this._removePagePath(childPath);
        }

        this._removePagePath(pagePath);
    }

    /*
     * Adds the page to tree if it does not exist already
     * if it exists, just replaces the new object but sorting
     * is according to the order of the old page itself
     */
    public addItem(page: WikiPage): void {
        const existingItem = this._allTreeItems[page.path];
        if (!existingItem) {
            const treeItem = this._treeMapper.createOneLevelTreeItems([page])[0];
            const parentPath = getParentPath(page.path);
            const parentItem = this._allTreeItems[parentPath];
            if (parentItem) {
                if (!parentItem.data.isParentPage) {
                    parentItem.data.isParentPage = true;
                }
                parentItem.expanded = true;
                parentItem.data.subPages.push(page);
            }
            this._addWikiPageTreeItem(page.path, treeItem);
            const itemBefore = this._reorderSiblings(page.path, Number.MAX_SAFE_INTEGER, page.order);
            this.add(treeItem, parentItem, itemBefore);
        } else {
            // if the page already exists in the tree dont add again - this is the scenario
            // when a folder without corresponding md page exists and we are now adding the md page for it
            existingItem.data = { ...page, pageName: getFileName(page.path) };
            this._wikiPathIgnoreCaseToPageMap[page.path.toLocaleLowerCase()] = page;
        }
    }

    public getNthChild(parentPath: string, n: number): WikiPage | undefined {
        let childItems;
        if (parentPath === RepoConstants.RootPath) {
            childItems = this._getRootItems();
        } else {
            const parent = this._allTreeItems[parentPath];
            if (parent) {
                childItems = parent.childItems;
            }
        }

        if (childItems && childItems.length >= n) {
            const item = childItems[n - 1];

            if (item) {
                return item.data;
            }
        }

        return undefined;
    }

    public getFirstConformantChild(parentPath: string): WikiPage | undefined {
        let childItems;
        if (parentPath === RepoConstants.RootPath) {
            childItems = this._getRootItems();
        } else {
            const parent = this._allTreeItems[parentPath];
            if (parent) {
                childItems = parent.childItems;
            }
        }

        if (childItems) {
            let currentIdx = 0;
            let itemToReturn: ITreeItem<WikiTreePage>;
            while (currentIdx < childItems.length) {
                itemToReturn = childItems[currentIdx];
                if (!itemToReturn.data.isNonConformant) {
                    return itemToReturn.data;
                }
                currentIdx++;
            }
        }

        return undefined;
    }

    // Given the page path, returns the index of that item in tree. use only when required
    // O(n): where n is total number of items in tree
    public getItemIndex(itemPath: string): number {
        const treeItem = this._allTreeItems[itemPath];
        if (treeItem) {
            return this._indexOf(treeItem);
        }
        return -1;
    }

    public getItemFromTreeIndex(index: number): WikiPage | undefined {
        if (index < 0 || index >= this.value.length) {
            return undefined;
        }

        return this.value[index].underlyingItem.data;
    }

    public toggleItem(itemPath: string, isExpanded?: boolean): void {
        const item = this._allTreeItems[itemPath];
        if (item) {
            if (item.data.isNonConformant) {
                return;
            }

            if (isExpanded === undefined || item.expanded !== isExpanded) {
                this.toggle(item);
            }
        }
    }

    public getItemFromTree = (path: string): WikiTreePage | undefined => {
        const item = this._allTreeItems[path];
        if (item) {
            return { ...item.data };
        }

        return undefined;
    };

    /**
     * Expands all parents of page to be selected next and returns its view index
     */
    public prepareNextPageToSelect = (path: string): number => {
        this.expandParentHierarchy(path);
        return this.getItemIndex(path);
    };

    // TODO: this will override subpages to given pages - we should merge
    public addChildItems(parentPath: string, childPages: WikiPage[]): void {
        // TODO: handle 'expanding' what happens if second call goes to fetch the items before the first one has returned
        // Task 1457081: Handle Expanding 'expand already in progress' for tree
        const parentItem = this._allTreeItems[parentPath];
        childPages = this._filterHiddenFiles(childPages);
        if (parentItem) {
            const parentItem = this._allTreeItems[parentPath];
            const pagesToAdd = childPages.filter(p => {
                return !this._allTreeItems[p.path];
            });
            const childItems = this._treeMapper.createOneLevelTreeItems(pagesToAdd);

            for (const item of childItems) {
                this._addWikiPageTreeItem(item.data.path, item);
            }

            if (childItems && childItems.length > 0) {
                parentItem.expanded = true;
                parentItem.data.subPages = childPages;
                this.splice(parentItem, undefined, [{ items: childItems }]);
            } else {
                this.toggle(parentItem);
            }
        } else {
            if ($DEBUG) {
                throw new Error("unexpected error - parent not found");
            }
        }
    }

    public expandParentHierarchy(path: string): void {
        const allParentPaths = getParentPaths(path);
        for (const parentPath of allParentPaths) {
            this.toggleItem(parentPath, true);
        }
    }

    // expensive call - avoid if you can
    // O(n)
    public getAllPages(): WikiPage[] {
        const allPages: WikiPage[] = [];

        for (const path in this._allTreeItems) {
            allPages.push(this._allTreeItems[path].data);
        }
        return allPages;
    }

    // Does ignore case lookup on wiki page path and returns one of the WikiPage matching the page path
    public getWikiPageForPagePathIgnoreCase = (pagePath: string): WikiPage | undefined => {
        return this._wikiPathIgnoreCaseToPageMap[pagePath.toLocaleLowerCase()];
    };

    /**
     * Reloads the entire page hierarchy for a given rootPath
     * To be used when a lot has changed within a page
     * @param rootPath
     * @param pages
     */
    public onPageHierarchyReloaded(rootPath: string, pages: WikiPage[]): void {
        const allTreeItems: { [key: string]: ITreeItem<WikiTreePage> } = {};
        pages = this._filterHiddenFiles(pages);
        this._treeMapper.createPageTree(pages, allTreeItems);
        const item = this._allTreeItems[rootPath];
        const parentItem = this._allTreeItems[getParentPath(rootPath)];
        for (const path in allTreeItems) {
            // we dont delete old hierarchy - just override
            // there might be an off chance that the original hierarchy is different
            // - it has some extra pages: we will add them in this step
            // - it misses some page - those will remain in map but not in hierarchy - worth taking a chance
            this._addWikiPageTreeItem(path, allTreeItems[path]);
        }
        const itemBefore = this._reorderSiblings(rootPath, Number.MAX_SAFE_INTEGER, item.data.order);

        this.remove(item, parentItem);
        this.add(this._allTreeItems[rootPath], parentItem, itemBefore);
    }

    public renameItem(oldPath: string, newPage: WikiPage): void {
        const oldItem: ITreeItem<WikiTreePage> = this._allTreeItems[oldPath];
        if (oldItem) {
            // TODO: this behaves haphazardly if hierarchy does not have order file
            this.moveItem(oldPath, newPage.path, newPage.order);
        }
    }

    /**
     * Changes page path from old to new for the entire hierarchy
     * Note: this does NOT update other fields like - gititempath, subpages...
     * this is just a temporary solution to update pages right away
     * reload the hierarchy for a given page and call onPageHierarchyReloaded to complete the flow
     * TODO: since this is a temporary solution, we should be good with just updating 1/2 level hierarchy - decide on the basis of perf
     * @param oldPath
     * @param newPath
     * @param newOrder
     */
    public moveItem(oldPath: string, newPath: string, newOrder: number): void {
        const oldItem: ITreeItem<WikiTreePage> = this._allTreeItems[oldPath];

        const oldParentPath = getParentPath(oldPath);
        const oldParent = this._allTreeItems[oldParentPath];
        const newParentPath = getParentPath(newPath);
        const newParent = this._allTreeItems[newParentPath];

        // create a page hierarchy
        // do a dfs over child items and change parent path from old to new
        const allTreeItems: { [key: string]: ITreeItem<WikiTreePage> } = {};
        const newHierarchy = this._treeMapper.replacePathInHierarchy(oldItem, newPath, allTreeItems);
        newHierarchy.data.order = newOrder; // todo reorder siblings
        newHierarchy.expanded = false;
        this.deletePage(oldPath);

        if (newParent) {
            newParent.data.subPages.push(newHierarchy.data);

            if (newParent.data.subPages.length === 1) {
                newParent.data.isParentPage = true;
            }
        }

        for (const path in allTreeItems) {
            // old items have been deleted in this.deletePage, now add the new ones
            this._addWikiPageTreeItem(path, allTreeItems[path]);
        }

        // reorder within siblings assuming this was the last node previously
        const itemBefore = this._reorderSiblings(newPath, Number.MAX_SAFE_INTEGER, newOrder);

        // add page
        this.add(newHierarchy, newParent, itemBefore);
        this.prepareNextPageToSelect(newPath);
    }

    public clear(): void {
        super.clear();
        this._allTreeItems = {};
        this._wikiPathIgnoreCaseToPageMap = {};
    }

    public getNextSibling(path: string, above: boolean = true): WikiPage | undefined {
        let siblings = this._getSiblings(path);
        if (!above) {
            siblings = siblings.reverse();
        }
        let itemBefore: WikiPage | undefined;

        for (const item of siblings) {
            if (item.data.path === path) {
                return itemBefore;
            }
            itemBefore = item.data;
        }
        return undefined;
    }

    private _filterHiddenFiles(pages: WikiPage[]): WikiPage[] {
        // not modifying the subpages array of a page i.e. the subarray might contain the files starting with dot
        return pages.filter(page => page.path.indexOf("/.") === -1 || page.path.indexOf(TemplateConstants.TemplateDirectory) === 0);
    }

    private _updateConformancyForPages(): void {
        for (const path in this._allTreeItems) {
            const page = this._allTreeItems[path].data;
            page.isNonConformant = this._isPageOrAncestorNonConformant(page);
        }
    }

    private _isPageOrAncestorNonConformant(page: WikiPage): boolean {
        if (page.isNonConformant) {
            return true;
        }

        let isNonConformant = false;
        const parentPaths = getParentPaths(page.path);
        parentPaths.every(
            (parentPath: string): boolean => {
                const parentItem = this._allTreeItems[parentPath];
                isNonConformant = isNonConformant || (parentItem && parentItem.data.isNonConformant);

                return !isNonConformant;
            }
        );

        return isNonConformant;
    }

    // Returns all siblings of the given item including the page itself in the order in which they are rendered
    private _getSiblings(path: string): ITreeItem<WikiTreePage>[] {
        const itemToReorder = this._allTreeItems[path];
        const parentPath = getParentPath(path);
        const parentItem = this._allTreeItems[parentPath]; // in case parentpath is '/' - parentitem is undefined

        let siblings: ITreeItem<WikiTreePage>[] = [];
        if (itemToReorder && parentPath === RepoConstants.RootPath) {
            // calculate all root pages:
            // using this.value and not this._allTreeItems as root items will always be in view
            siblings = this._getRootItems();
        } else if (parentItem && parentItem.childItems) {
            siblings = parentItem.childItems;
        }

        return siblings;
    }

    /**
     * Corrects the order of all siblings whena given page is moved from originalOrder to newOrder
     * @param path
     * @param originalOrder
     * @param newOrder
     * @returns the item after which current item will be placed(in the given hierarchy) - undefined if it will be at top
     */
    private _reorderSiblings(path: string, originalOrder: number, newOrder: number): ITreeItem<WikiTreePage> | undefined {
        let itemBefore: ITreeItem<WikiTreePage> | undefined;
        const siblings: ITreeItem<WikiTreePage>[] = this._getSiblings(path);

        if (siblings.length > 0) {
            for (const sibling of siblings) {
                sibling.data.order = sibling.data.order || 0;
                if (sibling.data.path === path) {
                    // Updating the page to be reordered
                    sibling.data.order = newOrder;
                } else {
                    // Updating other pages in-between the old and new order of the page
                    if (originalOrder > newOrder && sibling.data.order >= newOrder && sibling.data.order < originalOrder) {
                        sibling.data.order = boundedOrderIncrement(sibling.data.order);
                    } else if (originalOrder < newOrder && sibling.data.order <= newOrder && sibling.data.order > originalOrder) {
                        sibling.data.order = sibling.data.order - 1;
                    }
                }

                if (sibling.data.order === newOrder - 1) {
                    itemBefore = sibling;
                }
            }
        }

        return itemBefore;
    }

    private _hasChildItems(item: WikiTreePage | undefined): boolean {
        return item ? item.isParentPage : true; // if an item is found even without being a valid page, it can only be becasue of its children
    }

    private _getRootItems(): ITreeItem<WikiTreePage>[] {
        const childItems: ITreeItem<WikiTreePage>[] = [];
        this.value.forEach(item => {
            if (!item.parentItem) {
                childItems.push(item.underlyingItem);
            }
        });

        return childItems;
    }

    private _indexOf(treeItem: ITreeItem<WikiTreePage>, fromIndex: number = 0): number {
        // this method is taken from [here](https://dev.azure.com/mseng/_git/AzureDevOps?path=%2FVssf%2FWeb%2Fextensions%2Fvss-features%2Fvss-ui%2Fvss-tree%2FUtilities%2FTreeItemProvider.ts&version=GBmaster&line=414&lineStyle=plain&lineEnd=422&lineStartColumn=5&lineEndColumn=6)
        // in case we have a better way to do this
        for (let index = fromIndex; index < this.value.length; index++) {
            if (treeItem === this.value[index].underlyingItem) {
                return index;
            }
        }
        return -1;
    }

    /**
     * Returns the list of all page hierarchy of given parent in flat list format
     * @param parentPagePath
     */
    private _getAllSubPageList(parentPagePath: string): string[] {
        // instead of doing a tree traversal, we iterate over all pages and get paths starting with parentPagePath
        const childItems: string[] = [];
        if (!endsWith(parentPagePath, RepoConstants.PathSeparator)) {
            parentPagePath += RepoConstants.PathSeparator;
        }

        for (const path in this._allTreeItems) {
            if (startsWith(path, parentPagePath)) {
                childItems.push(path);
            }
        }
        return childItems;
    }

    private _addWikiPageTreeItem(path: string, treeItem: ITreeItem<WikiTreePage>): void {
        this._allTreeItems[path] = treeItem;
        this._wikiPathIgnoreCaseToPageMap[path.toLocaleLowerCase()] = treeItem.data;
    }

    private _addWikiPagesToWikiPathIgnoreCaseMap(wikiTreePagesMap: { [key: string]: ITreeItem<WikiTreePage> }) {
        for (const path of Object.keys(wikiTreePagesMap)) {
            if (path) {
                this._wikiPathIgnoreCaseToPageMap[path.toLocaleLowerCase()] = wikiTreePagesMap[path].data;
            }
        }
    }

    private _removePagePath(path: string): void {
        delete this._allTreeItems[path];
        // Adding check to make sure item exists before deleting from the wikiPagePaths map
        if (this._wikiPathIgnoreCaseToPageMap[path.toLocaleLowerCase()]) {
            delete this._wikiPathIgnoreCaseToPageMap[path.toLocaleLowerCase()];
        }
    }
}

interface PathTreeMapperOptions {
    hasChildItems?: (item: WikiTreePage) => boolean;
    isItemExpanded: (item: WikiTreePage) => boolean;
}

// export for UTs
export class PathTreeItemMapper {
    private _options: PathTreeMapperOptions;
    constructor(options: PathTreeMapperOptions) {
        this._options = options;
    }

    public createPageTree(pages: WikiPage[], allTreeItemsMapToFill: { [key: string]: ITreeItem<WikiTreePage> }): ITreeItem<WikiTreePage>[] {
        const root = new PathNode("");
        const createdPageMap: { [key: string]: WikiTreePage } = {};
        // create hierarchy
        for (const page of pages) {
            const wikiPageTreeItem = { ...page, pageName: getFileName(page.path) };
            createdPageMap[page.path] = wikiPageTreeItem;
            this._addItem(root, wikiPageTreeItem.path);
        }

        const treeItems: ITreeItem<WikiTreePage>[] = [];
        if (root.childNodes.length) {
            // now fetch the created hierarchy in sorted order
            this._visit(root.childNodes[0], treeItems, "", createdPageMap, allTreeItemsMapToFill);
        }

        return treeItems;
    }

    // same as above method, but only creates flat items from given items
    public createOneLevelTreeItems(pages: WikiPage[]): ITreeItem<WikiTreePage>[] {
        const treeItems: ITreeItem<WikiTreePage>[] = [];

        for (const page of pages) {
            const wikiPageTreeItem = { ...page, pageName: getFileName(page.path) };

            const item: ITreeItem<WikiTreePage> = {
                data: wikiPageTreeItem,
                expanded: this._options.isItemExpanded(wikiPageTreeItem),
                childItems: this._hasChildItems(wikiPageTreeItem) ? [] : undefined
            };

            treeItems.push(item);
        }

        treeItems.sort(_treeNodeSortOrderDecider);
        return treeItems;
    }

    public replacePathInHierarchy(
        oldHierarchyRoot: ITreeItem<WikiTreePage>,
        newPath: string,
        allTreeItemsMapToFill: { [key: string]: ITreeItem<WikiTreePage> }
    ): ITreeItem<WikiTreePage> {
        const childItems: ITreeItem<WikiTreePage>[] = [];
        this._visitAgain(oldHierarchyRoot, childItems, oldHierarchyRoot.data.path, newPath, allTreeItemsMapToFill);

        return childItems[0];
    }

    private _hasChildItems(item: WikiTreePage): boolean {
        return this._options.hasChildItems ? this._options.hasChildItems(item) : item.isParentPage;
    }

    private _addItem(root: PathNode, fodlerName: string): void {
        const result = _splitItemPath(fodlerName);

        let currentNode = root;
        for (const folder of result.folderParts) {
            // keep adding all folders till the last level
            currentNode = this._addItemIfRequired(currentNode, folder);
        }

        if (result.itemName) {
            this._addItemIfRequired(currentNode, result.itemName);
        }
    }

    private _addItemIfRequired(parentNode: PathNode, childName: string): PathNode {
        let childNode = parentNode.getChildNode(childName);

        if (!childNode) {
            childNode = parentNode.addChildNode(childName);
        }

        return childNode;
    }

    /**
     * Given a tree Root, re-visits the same hierarchy again (in DFS manner)  replacing
     * old path with newpath for each node. Adds the node to result array
     * @param root
     * @param result
     * @param oldPath
     * @param newPath
     * @param allTreeItemsMapToFill
     */
    private _visitAgain(
        root: ITreeItem<WikiTreePage>,
        result: ITreeItem<WikiTreePage>[],
        oldPath: string,
        newPath: string,
        allTreeItemsMapToFill: { [key: string]: ITreeItem<WikiTreePage> }
    ): void {
        const childrenList = root.childItems;
        const childItems: ITreeItem<WikiTreePage>[] = [];
        if (childrenList) {
            for (const child of childrenList) {
                this._visitAgain(child, childItems, oldPath, newPath, allTreeItemsMapToFill);
            }
        }

        const replacedPath = _replacePagePath(root.data.path, oldPath, newPath);
        const page: WikiTreePage = { ...root.data, path: replacedPath }; // create a new instance
        if (page.path === newPath) {
            page.pageName = getFileName(newPath);
        }

        const item: ITreeItem<WikiTreePage> = {
            data: page,
            expanded: root.expanded,
            childItems: root.childItems ? childItems : undefined // if previous root had no childitems, maintain that
        };
        allTreeItemsMapToFill[replacedPath] = item;

        result.push(item);
    }

    private _visit(
        node: PathNode,
        result: ITreeItem<WikiTreePage>[],
        parentPath: string,
        pageMap: { [key: string]: WikiTreePage },
        allTreeItemsMapToFill: { [key: string]: ITreeItem<WikiTreePage> }
    ): void {
        const isNotRoot = node.name !== RepoConstants.RootPath && node.name !== "";
        const fullFolderName = this._prependParentPath(parentPath, node.name);

        const childrenList = node.childNodes;
        const childItems: ITreeItem<WikiTreePage>[] = [];
        for (let i = 0; i < childrenList.length; ++i) {
            const child = childrenList[i];
            this._visit(child, childItems, fullFolderName, pageMap, allTreeItemsMapToFill);
        }

        childItems.sort(_treeNodeSortOrderDecider);
        if (isNotRoot) {
            const page: WikiTreePage = pageMap[fullFolderName];
            if (page) {
                const item: ITreeItem<WikiTreePage> = {
                    data: page,
                    expanded: this._options.isItemExpanded(page),
                    childItems: this._hasChildItems(page) ? childItems : undefined
                };

                result.push(item);
                // TODO: this is a weird wiki requirement where we need quick access to tree items at times
                // itemProvider only keeps currently visible items with it, rest are kept in hierarchy
                // if this map is not populated here, we will have to do another pass just to populate it
                allTreeItemsMapToFill[fullFolderName] = item;
            }
        } else {
            for (let i = 0; i < childItems.length; ++i) {
                result.push(childItems[i]);
            }
        }
    }

    /**
     * Prepends parentPath to childName. Handles scenarios like no or empty parent path
     */
    private _prependParentPath(parentPath: string | null, childName: string): string {
        if (parentPath !== null && parentPath != "") {
            if (parentPath.startsWith(RepoConstants.PathSeparator)) {
                if (parentPath === RepoConstants.PathSeparator) {
                    return parentPath + childName;
                }
                return parentPath + RepoConstants.PathSeparator + childName;
            } else {
                return RepoConstants.PathSeparator + parentPath + RepoConstants.PathSeparator + childName;
            }
        }

        return childName.startsWith(RepoConstants.PathSeparator) ? childName : RepoConstants.PathSeparator + childName;
    }
}

class PathNode {
    private _childMap: { [key: string]: PathNode } = {};
    public name: string;

    public childNodes: PathNode[] = [];

    constructor(name: string) {
        this.name = name;
    }

    public getChildNode(name: string): PathNode | undefined {
        return this._childMap[name];
    }

    public addChildNode(name: string): PathNode {
        const childNode = new PathNode(name);
        this._childMap[name] = childNode;
        this.childNodes.push(childNode);

        return childNode;
    }
}

interface SplitResult {
    folderParts: string[];
    itemName: string | undefined;
}

// export for UTs
export function _splitItemPath(key: string, separator: string = RepoConstants.PathSeparator): SplitResult {
    const parts = key.split(separator);

    if (parts[parts.length - 1] === "") {
        parts.pop();
    }
    const item = parts.pop();
    return { folderParts: parts, itemName: item };
}

// export for UTs
export function _treeNodeSortOrderDecider(page1: ITreeItem<WikiTreePage>, page2: ITreeItem<WikiTreePage>): number {
    const sortOrder1 = _calculatedSortOrderForHandlingIncludes(page1.data);
    const sortOrder2 = _calculatedSortOrderForHandlingIncludes(page2.data);

    // fallback logic for items that are not ordered - alphabetic
    if (sortOrder1 === sortOrder2) {
        return localeIgnoreCaseComparer(page1.data.pageName, page2.data.pageName);
    }

    return sortOrder1 - sortOrder2;
}

/**
 * WikiPage.Order contains positive integer values.
 * But we want to display templates folder at second number, independent of the order.
 * So multiple every order by 2. Such that {0, 1, 2, 3, 4, 5...} will become {0, 2, 4, 6, 8...}.
 * And assign template path order of 1.
 */
function _calculatedSortOrderForHandlingIncludes(wikiPage: WikiPage): number {
    if (wikiPage.path === TemplateConstants.TemplateDirectory) {
        return 1;
    }

    return wikiPage.order != undefined ? 2 * wikiPage.order : Number.MAX_SAFE_INTEGER;
}

/**
 * export for UTs
 * Given old and new parent path, returns the new path for the page
 * @param path
 * @param oldParentPath
 * @param newParentPath
 */
export function _replacePagePath(path: string, oldParentPath: string, newParentPath: string): string {
    if (path === oldParentPath) {
        return newParentPath;
    }

    if (!endsWith(oldParentPath, RepoConstants.PathSeparator)) {
        oldParentPath += RepoConstants.PathSeparator;
    }

    if (!endsWith(newParentPath, RepoConstants.PathSeparator)) {
        newParentPath += RepoConstants.PathSeparator;
    }

    return path.replace(oldParentPath, newParentPath);
}
